import { PropsWithChildren } from 'react'

import { AnalyticsQueryFilter } from '@hap/analytics'
import {
  ButtonProps,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@hap/components'
import { Timespan } from '@hap/utils'

import Link from '../link'
import { LinkButtonReactRouter } from './LinkButton'

const DEFAULT_TOOLTIP = 'View Interactions'

type Props = PropsWithChildren<{
  value?: string
  field?: string
  filters?: AnalyticsQueryFilter[]
  className?: string
  tooltip?: string
  size?: 'lg' | 'icon-sm'
  timespan?: Timespan.StaticTimespan
  variant?: ButtonProps['variant']
}>

export function DrilldownButton(props: Props) {
  return (
    <Tooltip>
      <TooltipTrigger>
        <LinkButtonReactRouter
          to="/explore"
          size={props.size || (props.children ? 'default' : 'icon')}
          variant={props.variant || 'secondary'}
          className={cn('group/button', props.className)}
          state={generateDrilldownParams(props)}
        >
          {props.children || (
            <Icon
              name="arrow-right"
              className="transition-transform group-hover/button:translate-x-1"
            />
          )}
        </LinkButtonReactRouter>
      </TooltipTrigger>
      <TooltipContent>{props.tooltip || DEFAULT_TOOLTIP}</TooltipContent>
    </Tooltip>
  )
}

export function DrilldownLink(props: Props) {
  return (
    <Tooltip>
      <TooltipTrigger>
        <DrilldownScaffold>
          {props.children}
          <span className="bg-secondary flex aspect-square scale-50 items-center rounded-md opacity-0 transition-transform group-hover:scale-100 group-hover:opacity-100">
            <Icon name="arrow-right" className="h-4" />
          </span>
        </DrilldownScaffold>
      </TooltipTrigger>
      <TooltipContent>{props.tooltip || DEFAULT_TOOLTIP}</TooltipContent>
    </Tooltip>
  )
}

export function DrilldownScaffold(props: Omit<Props, 'tooltip'>) {
  return (
    <Link
      to="/explore"
      className={cn('group flex items-center gap-2', props.className)}
      state={generateDrilldownParams(props)}
    >
      {props.children}
    </Link>
  )
}

const generateDrilldownParams = (props: Props) =>
  props.filters
    ? { query: props.filters, timespan: props.timespan }
    : props.field && props.value
      ? {
          query: [
            { field: props.field, op: 'in' as const, values: [props.value] },
          ] as AnalyticsQueryFilter[],
        }
      : undefined
